import { useEffect, useMemo, useState } from "react";
import adminApi, { fmtDate } from "../../lib/adminApi";
import { fmtUsd, formatErr } from "../../lib/api";
import { MagnifyingGlass, Coins, Prohibit, ArrowCounterClockwise, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "Все" },
  { key: "active", label: "Активные" },
  { key: "blocked", label: "Заблок." },
  { key: "funded", label: "С депозитом" },
];
const PAGE = 25;

export default function AdminTraders() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [balanceUser, setBalanceUser] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.get(`/admin/users`, { params: { q, status, skip, limit: PAGE } })
      .then((r) => { setUsers(r.data.users); setTotal(r.data.total); })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, skip]);

  const doBlock = async (u, block) => {
    try {
      await adminApi.post(`/admin/users/${u.id}/block`, { block });
      toast.success(block ? "Трейдер заблокирован" : "Трейдер разблокирован");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doReset = async (u) => {
    try {
      const { data } = await adminApi.post(`/admin/users/${u.id}/reset`);
      toast.success(`Сброс: закрыто ${data.closed_positions} поз., отменено ${data.cancelled_orders} орд.`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const pageInfo = useMemo(() => {
    const from = total === 0 ? 0 : skip + 1;
    const to = Math.min(skip + PAGE, total);
    return `${from}–${to} из ${total}`;
  }, [skip, total]);

  return (
    <div data-testid="admin-traders">
      <h2 className="text-lg font-semibold text-white mb-4">Трейдеры</h2>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Поиск по имени, email, Telegram ID…"
            className="h-10 pl-9 bg-[#111827] border-[#1F2937] text-white rounded-lg" data-testid="traders-search" />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => { setSkip(0); setStatus(f.key); }}
              data-testid={`traders-filter-${f.key}`}
              className={`px-3 h-10 rounded-lg text-xs font-medium transition-colors ${status === f.key ? "bg-[#E3B341]/15 text-[#E3B341] ring-1 ring-inset ring-[#E3B341]/30" : "bg-[#111827] text-[#6B7280] hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#1F2937]">
                <th className="text-left px-4 py-2.5">Трейдер</th>
                <th className="text-left px-4 py-2.5">Источник</th>
                <th className="text-right px-4 py-2.5">Баланс</th>
                <th className="text-right px-4 py-2.5">Депозиты</th>
                <th className="text-right px-4 py-2.5">Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1F2937]/60 hover:bg-[#0A0E17]/40" data-testid={`trader-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{u.name || "—"}</span>
                      {u.blocked && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F6465D]/15 text-[#F6465D]">BLOCK</span>}
                    </div>
                    <p className="text-[11px] text-[#6B7280]">{u.email}</p>
                  </td>
                  <td className="px-4 py-3 text-[#9CA3AF] text-xs">
                    {u.telegram_id ? `TG ${u.telegram_id}` : "Web"}
                    <p className="text-[10px] text-[#4B5563]">{fmtDate(u.created_at)}</p>
                  </td>
                  <td className="px-4 py-3 text-right mono text-[#0ECB81]">${fmtUsd(u.balance)}</td>
                  <td className="px-4 py-3 text-right mono text-[#9CA3AF]">${fmtUsd(u.total_deposits || 0)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => setBalanceUser(u)}
                        className="h-8 text-[#E3B341] hover:bg-[#E3B341]/10" data-testid={`trader-balance-btn-${u.id}`}>
                        <Coins size={15} className="mr-1" /> Баланс
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => doBlock(u, !u.blocked)}
                        className={`h-8 ${u.blocked ? "text-[#0ECB81] hover:bg-[#0ECB81]/10" : "text-[#F6465D] hover:bg-[#F6465D]/10"}`}
                        data-testid={`trader-block-btn-${u.id}`}>
                        <Prohibit size={15} className="mr-1" /> {u.blocked ? "Разблок." : "Блок"}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 text-[#9CA3AF] hover:bg-white/5" data-testid={`trader-reset-btn-${u.id}`}>
                            <ArrowCounterClockwise size={15} className="mr-1" /> Сброс
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#111827] border-[#1F2937] text-white">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Полный сброс аккаунта?</AlertDialogTitle>
                            <AlertDialogDescription className="text-[#9CA3AF]">
                              Все открытые позиции будут закрыты, ордера отменены, а баланс обнулён для «{u.name || u.email}».
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-[#1F2937] text-white hover:bg-white/5">Отмена</AlertDialogCancel>
                            <AlertDialogAction onClick={() => doReset(u)} className="bg-[#F6465D] hover:bg-[#d93a4f] text-white" data-testid={`trader-reset-confirm-${u.id}`}>
                              Сбросить
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6B7280]">Ничего не найдено</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#1F2937]">
          <span className="text-xs text-[#6B7280]" data-testid="traders-page-info">{pageInfo}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}
              className="h-8 text-[#9CA3AF] disabled:opacity-30" data-testid="traders-prev">
              <CaretLeft size={16} />
            </Button>
            <Button size="sm" variant="ghost" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}
              className="h-8 text-[#9CA3AF] disabled:opacity-30" data-testid="traders-next">
              <CaretRight size={16} />
            </Button>
          </div>
        </div>
      </div>

      <BalanceDialog user={balanceUser} onClose={() => setBalanceUser(null)} onDone={load} />
    </div>
  );
}

function BalanceDialog({ user, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setAmount(""); setReason(""); }, [user]);

  const submit = async (sign) => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error("Введите сумму больше 0"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/users/${user.id}/balance`, { delta: sign * val, reason });
      toast.success(`Новый баланс: $${fmtUsd(data.balance)}`);
      onDone();
      onClose();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-white" data-testid="balance-dialog">
        <DialogHeader>
          <DialogTitle>Изменить баланс</DialogTitle>
          <DialogDescription className="text-[#6B7280]">Начислите или спишите виртуальный баланс трейдера.</DialogDescription>
        </DialogHeader>
        {user && (
          <div className="space-y-3">
            <div className="text-sm text-[#9CA3AF]">
              {user.name || user.email} · текущий баланс <span className="mono text-[#0ECB81]">${fmtUsd(user.balance)}</span>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.18em] text-[#6B7280] uppercase">Сумма (USDT)</label>
              <Input type="number" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="1000" className="mt-1 h-10 bg-[#0A0E17] border-[#1F2937] text-white rounded-lg" data-testid="balance-amount-input" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.18em] text-[#6B7280] uppercase">Причина (опц.)</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Бонус за депозит"
                className="mt-1 h-10 bg-[#0A0E17] border-[#1F2937] text-white rounded-lg" data-testid="balance-reason-input" />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button disabled={busy} onClick={() => submit(-1)} variant="ghost"
            className="text-[#F6465D] hover:bg-[#F6465D]/10" data-testid="balance-debit-btn">Списать</Button>
          <Button disabled={busy} onClick={() => submit(1)}
            className="bg-[#0ECB81] hover:bg-[#0bb673] text-[#0A0E17] font-semibold" data-testid="balance-credit-btn">Начислить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
